import { useCallback, useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { trackEvent, getUserDetails, getWindow } from '@surfline/web-common';

import { doUpdateUserSettings } from 'actions/user';
import usePreferredForecastView from 'hooks/usePreferredForecastView';
import { useUserPreferredForecastView } from 'selectors/user';
import { useAppDispatch, useAppSelector } from 'stores/hooks';
import { useURLState } from 'contexts/URLStateContext';

interface UseTableViewStateProps {
  spotId?: string;
  spotName?: string;
  subregionId?: string;
  subregionName?: string;
  isCustomForecast?: boolean;
}

interface UseTableViewStateReturn {
  isTableViewMode: boolean;
  toggleTableView: () => void;
  isHydrated: boolean;
}

const useTableViewState = ({
  spotId,
  spotName,
  subregionId,
  subregionName,
  isCustomForecast = false,
}: UseTableViewStateProps = {}): UseTableViewStateReturn => {
  const router = useRouter();
  const { query } = router;
  const dispatch = useAppDispatch();
  const { updateViewPreference, isHydrated } = useURLState();

  const userDetails = useAppSelector(getUserDetails);
  const isAnonymous = !userDetails;
  const preferredForecastView = useUserPreferredForecastView();
  const { preferredForecastViewLocalStorage, togglePreferredForecastView } =
    usePreferredForecastView(isAnonymous);
  const userPreferences = useMemo(
    () => ({
      isAnonymous,
      preferredForecastView: preferredForecastView || '',
      preferredForecastViewLocalStorage: preferredForecastViewLocalStorage || '',
    }),
    [isAnonymous, preferredForecastView, preferredForecastViewLocalStorage],
  );

  const [hasPerformedInitialSync, setHasPerformedInitialSync] = useState(false);

  const isTableQuery = useMemo(() => query?.view === 'table', [query?.view]);
  const isTableViewMode = useMemo(() => {
    // During SSR or before hydration, always default to false to prevent mismatch
    if (!isHydrated) {
      return false;
    }

    return (
      isTableQuery ||
      (userPreferences.isAnonymous
        ? userPreferences.preferredForecastViewLocalStorage === 'TABLE'
        : userPreferences.preferredForecastView === 'TABLE')
    );
  }, [isHydrated, isTableQuery, userPreferences]);

  useEffect(() => {
    if (!isHydrated || !router.isReady || hasPerformedInitialSync) {
      return;
    }

    setHasPerformedInitialSync(true);
    if (isCustomForecast) {
      if (isTableViewMode && !isTableQuery) {
        updateViewPreference({ isTableView: true });
      }
      return;
    }

    if (spotId && spotName && isTableViewMode && !isTableQuery) {
      updateViewPreference({
        isTableView: true,
        spotId,
        spotName,
      });
    }
  }, [
    isHydrated,
    router.isReady,
    hasPerformedInitialSync,
    isCustomForecast,
    isTableViewMode,
    isTableQuery,
    spotId,
    spotName,
    updateViewPreference,
  ]);

  const toggleTableView = useCallback(() => {
    const win = getWindow();
    const newViewMode = !isTableViewMode;
    const newPreference = isTableViewMode ? 'GRAPH' : 'TABLE';

    if (win && !isCustomForecast) {
      trackEvent('Clicked Toggle', {
        toggleType: 'table view',
        toggleStart: isTableViewMode ? 'table' : 'graph',
        toggleEnd: newViewMode ? 'table' : 'graph',
        pageName: 'Spot Forecast',
        spotName,
        spotId,
        subregionName,
        subregionId,
      });
    }

    if (userPreferences.isAnonymous) {
      togglePreferredForecastView(newPreference);
    } else {
      dispatch(doUpdateUserSettings({ preferredForecastView: newPreference }));
    }

    updateViewPreference({
      isTableView: newViewMode,
      spotId,
      spotName,
    });
  }, [
    isTableViewMode,
    isCustomForecast,
    spotName,
    spotId,
    subregionName,
    subregionId,
    userPreferences.isAnonymous,
    togglePreferredForecastView,
    dispatch,
    updateViewPreference,
  ]);

  return useMemo(
    () => ({
      isTableViewMode,
      toggleTableView,
      isHydrated,
    }),
    [isTableViewMode, toggleTableView, isHydrated],
  );
};

export default useTableViewState;
